import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  ArrowLeft, CheckCircle, Calendar, Clock, MapPin, User, Mail, Phone, 
  Loader2, AlertCircle, Star, Users
} from 'lucide-react';
import MemberLookup, { MemberMatchedBanner } from './MemberLookup';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format helpers for Portal session_template shape (day_of_week, start_time as "HH:MM")
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const formatTime12h = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const displayH = h % 12 || 12;
  return `${displayH}:${String(m).padStart(2, '0')} ${period}`;
};

const buildSlotLabel = (slot) =>
  `${slot.day} ${slot.time} at ${slot.location} (${slot.ageGroup}${slot.gender && slot.gender !== 'mixed' ? ` · ${slot.gender}` : ''} · ${slot.termName})`;

const TrialSignUp = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Member lookup state
  const [matchedParent, setMatchedParent] = useState(null);
  const [matchedChildren, setMatchedChildren] = useState([]);
  const [showStep1Form, setShowStep1Form] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    childName: '',
    childDob: '',
    childGender: '',
    preferredSlotId: '',   // Portal session_template_id
    secondarySlotId: '',   // Portal session_template_id
    experience: '',
    notes: '',
  });

  // Portal-driven trial slots (fetched from /api/academy/terms + /api/academy/sessions/{term_id})
  const [allSlots, setAllSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState(null);

  // Indemnity (Portal enforces mandatory acceptance)
  const [consentTexts, setConsentTexts] = useState(null);
  const [indemnityAccepted, setIndemnityAccepted] = useState(false);
  const [indemnityExpanded, setIndemnityExpanded] = useState(false);

  // Fetch live trial slots + indemnity text from Portal on mount
  useEffect(() => {
    const loadPortalData = async () => {
      setSlotsLoading(true);
      setSlotsError(null);
      try {
        // 1. Fetch consent texts (for indemnity block)
        fetch(`${API_URL}/api/academy/consent-texts`)
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => data && setConsentTexts(data))
          .catch(() => {});

        // 2. Fetch active terms → for each term, fetch sessions → flatten
        const termsRes = await fetch(`${API_URL}/api/academy/terms`);
        if (!termsRes.ok) throw new Error('Failed to load terms');
        const termsData = await termsRes.json();
        const terms = Array.isArray(termsData) ? termsData : (termsData.terms || []);

        const perTermFetches = terms.map(async (term) => {
          const r = await fetch(`${API_URL}/api/academy/sessions/${term.id}`);
          if (!r.ok) return [];
          const data = await r.json();
          const list = data.templates || data.sessions || (Array.isArray(data) ? data : []);
          return list.map((s) => ({
            id: s.id,
            termId: term.id,
            termName: term.name,
            day: s.day_name || DAY_NAMES[s.day_of_week] || '',
            dayOfWeek: s.day_of_week,
            time: `${formatTime12h(s.start_time)} - ${formatTime12h(s.end_time)}`,
            startTime: s.start_time,
            location: s.location || 'Dempsey Field',
            sessionType: s.session_type || 'Training',
            gender: s.gender || 'mixed',
            minAge: s.min_age,
            maxAge: s.max_age,
            ageGroup: s.age_group_name || (s.max_age ? `U${s.max_age}` : ''),
          }));
        });
        const flat = (await Promise.all(perTermFetches)).flat();
        setAllSlots(flat);
      } catch (err) {
        console.error('[TRIAL] Failed to load Portal data:', err);
        setSlotsError('Unable to load available trial sessions. Please refresh or try again shortly.');
      } finally {
        setSlotsLoading(false);
      }
    };
    loadPortalData();
  }, []);

  const handleMatch = (parent, children) => {
    setMatchedParent(parent);
    setMatchedChildren(children || []);
    setFormData((f) => ({
      ...f,
      parentName: parent?.name || f.parentName,
      parentEmail: parent?.email || f.parentEmail,
      parentPhone: parent?.phone || f.parentPhone,
    }));
    setShowStep1Form(true);
  };

  const handleSkipLookup = () => setShowStep1Form(true);

  const handleChangeMatch = () => {
    setMatchedParent(null);
    setMatchedChildren([]);
    setShowStep1Form(false);
  };

  const handleSelectExistingChild = (child) => {
    setFormData((f) => ({
      ...f,
      childName: child.name || '',
      childDob: child.birthdate || child.dob || '',
      childGender: child.gender || f.childGender,
    }));
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  // Calculate age from DOB
  const calculateAge = (dob) => {
    if (!dob) return null;
    const today = new Date();
    const birth = new Date(dob);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const getAgeGroup = (dob) => {
    const age = calculateAge(dob);
    if (age === null) return '';
    return `U${age + 1}`;
  };

  const validateStep1 = () => {
    if (!formData.parentName.trim()) return 'Please enter parent name';
    if (!formData.parentEmail.trim()) return 'Please enter email address';
    if (!formData.parentEmail.includes('@')) return 'Please enter a valid email';
    if (!formData.parentPhone.trim()) return 'Please enter phone number';
    return null;
  };

  const validateStep2 = () => {
    if (!formData.childName.trim()) return 'Please enter child name';
    if (!formData.childDob) return 'Please enter date of birth';
    if (!formData.childGender) return 'Please select gender';
    const age = calculateAge(formData.childDob);
    if (age < 4 || age > 17) return 'Child must be between 4-17 years old';
    return null;
  };

  const validateStep3 = () => {
    if (!formData.preferredSlotId) return 'Please select a preferred training slot';
    if (!indemnityAccepted) return 'Please read and accept the Indemnity Waiver to continue';
    return null;
  };

  const handleNext = () => {
    let validationError = null;
    if (step === 1) validationError = validateStep1();
    if (step === 2) validationError = validateStep2();
    if (step === 3) validationError = validateStep3();
    
    if (validationError) {
      setError(validationError);
      return;
    }
    setStep(step + 1);
  };

  const handleSubmit = async () => {
    const validationError = validateStep3();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    // Resolve full slot objects for payload
    const preferredSlot = allSlots.find((s) => s.id === formData.preferredSlotId);
    const secondarySlot = formData.secondarySlotId
      ? allSlots.find((s) => s.id === formData.secondarySlotId)
      : null;

    try {
      const response = await fetch(`${API_URL}/api/trial/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          parent_name: formData.parentName,
          parent_email: formData.parentEmail,
          parent_phone: formData.parentPhone,
          child_name: formData.childName,
          child_dob: formData.childDob,
          child_gender: formData.childGender,
          child_age: calculateAge(formData.childDob),
          age_group: getAgeGroup(formData.childDob),
          preferred_slot_session_template_id: preferredSlot?.id || null,
          preferred_slot_label: preferredSlot ? buildSlotLabel(preferredSlot) : null,
          secondary_slot_session_template_id: secondarySlot?.id || null,
          secondary_slot_label: secondarySlot ? buildSlotLabel(secondarySlot) : null,
          experience_level: formData.experience || 'Not specified',
          notes: formData.notes || '',
          indemnity_accepted: indemnityAccepted,
          indemnity_version: consentTexts?.indemnity?.version || null,
          indemnity_accepted_at: new Date().toISOString(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit trial request');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Success screen
  if (success) {
    return (
      <div className="min-h-screen bg-background py-8 px-4">
        <div className="max-w-lg mx-auto">
          <Card className="bg-gradient-card gold-border text-center">
            <CardContent className="pt-8 pb-8">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-primary mb-2">Trial Request Submitted!</h2>
              <p className="text-muted-foreground mb-6">
                Thank you for your interest in SCC Football Academy. We&apos;ll contact you within 24-48 hours to confirm your trial session.
              </p>
              
              <div className="bg-charcoal/50 rounded-lg p-4 text-left mb-6">
                <p className="text-sm text-muted-foreground mb-2">Request Summary:</p>
                <p className="text-card-foreground"><strong>{formData.childName}</strong> ({getAgeGroup(formData.childDob)})</p>
                <p className="text-sm text-muted-foreground">
                  Preferred: {allSlots.find(s => s.id === formData.preferredSlotId)?.day} {allSlots.find(s => s.id === formData.preferredSlotId)?.time}
                </p>
              </div>

              <div className="space-y-3">
                <Button onClick={() => navigate('/')} className="w-full gold-button">
                  Back to Home
                </Button>
                <Button variant="outline" onClick={() => navigate('/register-academy')} className="w-full">
                  View Full Registration
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-primary">Book a Free Trial</h1>
            <p className="text-muted-foreground">Experience SCC Football Academy</p>
          </div>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? 'bg-primary text-primary-foreground' : 'bg-charcoal text-muted-foreground'
              }`}>
                {step > s ? <CheckCircle className="h-4 w-4" /> : s}
              </div>
              {s < 3 && <div className={`w-12 h-0.5 ${step > s ? 'bg-primary' : 'bg-charcoal'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Benefits Banner */}
        <Card className="bg-green-500/10 border-green-500/30 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Star className="h-5 w-5 text-green-400" />
              <div>
                <p className="text-green-400 font-medium">Your Free Trial Includes:</p>
                <p className="text-sm text-muted-foreground">
                  1.5 hour session • Professional coaching • All equipment provided • No obligation
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Error Display */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Step 1: Parent Details */}
        {step === 1 && !showStep1Form && (
          <MemberLookup onMatch={handleMatch} onSkip={handleSkipLookup} />
        )}

        {step === 1 && showStep1Form && (
          <div className="space-y-4">
            {matchedParent && (
              <MemberMatchedBanner parent={matchedParent} onChange={handleChangeMatch} />
            )}
            {matchedParent && matchedChildren.length > 0 && (
              <Card className="bg-gradient-card border-green-500/30">
                <CardContent className="p-4">
                  <div className="text-sm text-card-foreground mb-2">Quick-pick an existing child:</div>
                  <div className="flex flex-wrap gap-2">
                    {matchedChildren.map((c) => (
                      <Button
                        key={c.player_id || c.id || c.name}
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleSelectExistingChild(c)}
                        className="text-xs"
                        data-testid={`trial-existing-child-${c.player_id || c.id || c.name}`}
                      >
                        {c.name}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          <Card className="bg-gradient-card gold-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Parent/Guardian Details
              </CardTitle>
              <CardDescription>We&apos;ll use this to contact you about the trial</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="parentName">Full Name *</Label>
                <Input
                  id="parentName"
                  value={formData.parentName}
                  onChange={(e) => updateField('parentName', e.target.value)}
                  placeholder="Enter your full name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="parentEmail">Email Address *</Label>
                <Input
                  id="parentEmail"
                  type="email"
                  value={formData.parentEmail}
                  onChange={(e) => updateField('parentEmail', e.target.value)}
                  placeholder="your@email.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="parentPhone">Phone Number *</Label>
                <Input
                  id="parentPhone"
                  type="tel"
                  value={formData.parentPhone}
                  onChange={(e) => updateField('parentPhone', e.target.value)}
                  placeholder="+65 9xxx xxxx"
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {/* Step 2: Child Details */}
        {step === 2 && (
          <Card className="bg-gradient-card gold-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Child Details
              </CardTitle>
              <CardDescription>Tell us about your child</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="childName">Child&apos;s Name *</Label>
                <Input
                  id="childName"
                  value={formData.childName}
                  onChange={(e) => updateField('childName', e.target.value)}
                  placeholder="Enter child's full name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="childDob">Date of Birth *</Label>
                <Input
                  id="childDob"
                  type="date"
                  value={formData.childDob}
                  onChange={(e) => updateField('childDob', e.target.value)}
                  className="mt-1"
                />
                {formData.childDob && (
                  <div className="flex gap-2 mt-2">
                    <Badge variant="outline">Age: {calculateAge(formData.childDob)}</Badge>
                    <Badge className="bg-primary/20 text-primary">{getAgeGroup(formData.childDob)}</Badge>
                  </div>
                )}
              </div>
              <div>
                <Label>Gender *</Label>
                <Select value={formData.childGender} onValueChange={(v) => updateField('childGender', v)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Football Experience</Label>
                <Select value={formData.experience} onValueChange={(v) => updateField('experience', v)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select experience level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No experience (beginner)</SelectItem>
                    <SelectItem value="some">Some experience (played casually)</SelectItem>
                    <SelectItem value="academy">Academy experience (trained before)</SelectItem>
                    <SelectItem value="competitive">Competitive experience (played in leagues)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Select Training Slot */}
        {step === 3 && (() => {
          // Filter Portal-fetched sessions by child's age & gender
          const childAge = calculateAge(formData.childDob);
          const childGender = (formData.childGender || '').toLowerCase();
          const eligibleSlots = allSlots.filter((s) => {
            if (childAge != null && s.minAge != null && s.maxAge != null) {
              if (childAge < s.minAge || childAge > s.maxAge) return false;
            }
            if (childGender && s.gender && s.gender !== 'mixed' && s.gender.toLowerCase() !== childGender) {
              return false;
            }
            return true;
          });
          // Group by term for readability
          const grouped = eligibleSlots.reduce((acc, s) => {
            (acc[s.termName] = acc[s.termName] || []).push(s);
            return acc;
          }, {});
          return (
          <Card className="bg-gradient-card gold-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Select Training Slot
              </CardTitle>
              <CardDescription>
                Your child will attend one of our regular term sessions as a free trial (no payment).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {slotsLoading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="slots-loading">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading available sessions from Portal…
                </div>
              )}
              {slotsError && (
                <div className="p-3 rounded bg-red-500/10 border border-red-500/30 text-sm text-red-300" data-testid="slots-error">
                  {slotsError}
                </div>
              )}
              {!slotsLoading && !slotsError && eligibleSlots.length === 0 && (
                <div className="p-3 rounded bg-amber-500/10 border border-amber-500/30 text-sm text-amber-300" data-testid="no-eligible-slots">
                  We couldn&apos;t find any active sessions matching {formData.childName || 'your child'}&apos;s age and gender. Please contact academy@sccfootball.com and we&apos;ll help you find a suitable trial.
                </div>
              )}

              {!slotsLoading && eligibleSlots.length > 0 && (
                <div>
                  <Label className="text-primary mb-3 block">Preferred Slot *</Label>
                  <div className="space-y-4" data-testid="preferred-slot-list">
                    {Object.entries(grouped).map(([termName, slots]) => (
                      <div key={termName}>
                        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">{termName}</p>
                        <div className="grid gap-2">
                          {slots.map((slot) => (
                            <button
                              key={slot.id}
                              type="button"
                              onClick={() => updateField('preferredSlotId', slot.id)}
                              data-testid={`slot-${slot.id}`}
                              className={`p-3 rounded-lg border text-left transition-all ${
                                formData.preferredSlotId === slot.id
                                  ? 'border-primary bg-primary/10 ring-1 ring-primary/50'
                                  : 'border-border/50 hover:border-primary/50 bg-charcoal/30'
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-medium text-card-foreground">{slot.day}</p>
                                    <Badge variant="outline" className="text-xs">{slot.ageGroup}</Badge>
                                    {slot.gender && slot.gender !== 'mixed' && (
                                      <Badge variant="outline" className="text-xs capitalize">{slot.gender}</Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground flex-wrap">
                                    <span className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" /> {slot.time}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <MapPin className="h-3 w-3" /> {slot.location}
                                    </span>
                                  </div>
                                </div>
                                {formData.preferredSlotId === slot.id && (
                                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!slotsLoading && eligibleSlots.length > 1 && (
                <div>
                  <Label>Secondary Slot (optional)</Label>
                  <Select value={formData.secondarySlotId} onValueChange={(v) => updateField('secondarySlotId', v)}>
                    <SelectTrigger className="mt-1" data-testid="secondary-slot-trigger">
                      <SelectValue placeholder="Select a backup option" />
                    </SelectTrigger>
                    <SelectContent>
                      {eligibleSlots.filter((s) => s.id !== formData.preferredSlotId).map((slot) => (
                        <SelectItem key={slot.id} value={slot.id}>
                          {slot.day} {slot.time} · {slot.ageGroup} · {slot.termName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>Additional Notes (optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  placeholder="Any questions or special requirements?"
                  className="mt-1"
                  rows={3}
                />
              </div>

              {/* Indemnity — Portal-driven, mandatory */}
              {consentTexts?.indemnity && (
                <div className="border-t border-border pt-4 mt-2" data-testid="indemnity-block">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="trial-indemnity"
                      checked={indemnityAccepted}
                      onChange={(e) => setIndemnityAccepted(e.target.checked)}
                      className="mt-1 accent-primary"
                      data-testid="indemnity-checkbox"
                    />
                    <div className="flex-1">
                      <label htmlFor="trial-indemnity" className="cursor-pointer font-medium text-card-foreground">
                        {consentTexts.indemnity.title || 'Indemnity Waiver'} <span className="text-red-400">*</span>
                      </label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Required by SCC to proceed with any trial session.
                        {consentTexts.indemnity.version && (
                          <span className="ml-1 opacity-70">
                            ({String(consentTexts.indemnity.version).startsWith('v') ? '' : 'v'}{consentTexts.indemnity.version})
                          </span>
                        )}
                      </p>
                      <button
                        type="button"
                        onClick={() => setIndemnityExpanded(!indemnityExpanded)}
                        className="text-xs text-primary hover:underline mt-2"
                        data-testid="indemnity-toggle"
                      >
                        {indemnityExpanded ? 'Hide full text' : 'Read the full Indemnity Waiver'}
                      </button>
                      {indemnityExpanded && (
                        <div
                          className="mt-3 p-3 bg-charcoal/50 rounded border border-border/50 text-xs text-muted-foreground max-h-64 overflow-y-auto whitespace-pre-wrap"
                          data-testid="indemnity-full-text"
                        >
                          {consentTexts.indemnity.content || consentTexts.indemnity.text || 'Indemnity text unavailable.'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          );
        })()}

        {/* Navigation Buttons */}
        <div className="flex gap-3 mt-6">
          {step > 1 && (
            <Button variant="outline" onClick={() => setStep(step - 1)} className="flex-1">
              Back
            </Button>
          )}
          {step < 3 ? (
            <Button onClick={handleNext} className="flex-1 gold-button">
              Continue
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              disabled={loading}
              className="flex-1 gold-button"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Trial Request'
              )}
            </Button>
          )}
        </div>

        {/* Footer Info */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          By submitting, you agree to be contacted by SCC Football Academy regarding your trial request.
        </p>
      </div>
    </div>
  );
};

export default TrialSignUp;
